import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/lib/api';

export default function DangerZone() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete('/auth/account');
      toast.success('Votre compte a été supprimé. Vos données ont été anonymisées.');
      await logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression du compte');
      setDeleting(false);
    }
  };

  return (
    <div className="mt-8 bg-red-50/50 rounded-xl border border-red-200 p-6" data-testid="danger-zone">
      <h3 className="font-serif text-lg font-medium flex items-center gap-2 mb-2 text-red-700">
        <AlertTriangle className="h-5 w-5" /> Informations légales
      </h3>
      <p className="text-sm text-muted-foreground font-sans mb-4">
        Conformément au RGPD, vous pouvez demander la suppression de votre compte et l'anonymisation
        de vos données personnelles. Vos commandes antérieures sont conservées pour des raisons
        comptables légales, mais sans aucune information personnelle.
      </p>
      <Button
        variant="outline"
        className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
        onClick={() => setConfirmOpen(true)}
        data-testid="delete-account-button"
      >
        <X className="h-4 w-4 mr-2" /> Supprimer mon compte
      </Button>

      <Dialog open={confirmOpen} onOpenChange={(open) => { setConfirmOpen(open); if (!open) setConfirmText(''); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Confirmer la suppression définitive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground font-sans">
              Cette action est <strong>irréversible</strong>. Pour confirmer, tapez{' '}
              <code className="bg-red-50 px-1.5 py-0.5 rounded text-red-700">SUPPRIMER</code> ci-dessous.
            </p>
            <Input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              data-testid="delete-account-confirm-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="delete-cancel">
                Annuler
              </Button>
              <Button
                variant="outline"
                className="bg-red-600 text-white hover:bg-red-700 border-red-700"
                disabled={confirmText !== 'SUPPRIMER' || deleting}
                onClick={handleDelete}
                data-testid="delete-confirm"
              >
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
